import { APIDivulgador } from '@app/services/divulgador-api';
import { ClientBusiness } from '@app/types';
import { useQueryParamsWithNextRouter } from '@app/utils/routing';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { NotAccountOwnerError } from './errors';
import { ClientAreaStorage } from './storage';

type ClientAreaContextData = {
  client: ClientBusiness | null;
  setClient: Dispatch<SetStateAction<ClientBusiness | null>>;
  isLoggedIn: boolean;
  logOut: () => void;
  fetchBusiness: (token: string, cliqueiAcheiId: string | number) => Promise<void>;
};

const ClientAreaContext = createContext({} as ClientAreaContextData);

type Props = {
  children: ReactNode;
};

export function ClientAreaProvider({ children }: Props) {
  const [client, setClient] = useState<ClientBusiness | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const { localId } = useQueryParamsWithNextRouter();

  const fetchBusiness = useCallback(async (token: string, currentCliqueiAcheiId: string | number) => {
    const { data: allBusinesses } = await APIDivulgador.fetchAllBusinessesData(token);
    const currentBusiness = allBusinesses.find((b) => Number(b.ClienteId) === Number(currentCliqueiAcheiId));
    if (!currentBusiness) throw new NotAccountOwnerError();
    setToken(token);
    ClientAreaStorage.saveToken(currentBusiness.ClienteId, token);
    setClient(currentBusiness);
  }, []);

  useEffect(() => {
    if (!localId) return;
    const token = ClientAreaStorage.getToken(localId);
    if (!token) return;
    try {
      fetchBusiness(token, localId);
    } catch (error) {}
  }, [localId]);

  const logOut = useCallback(() => {
    if (client) ClientAreaStorage.removeToken(client.ClienteId);
    setClient(null);
    setToken(null);
  }, [client]);

  const isLoggedIn = Boolean(!!client || !!token);

  return (
    <ClientAreaContext.Provider value={{ client, setClient, logOut, isLoggedIn, fetchBusiness }}>
      {children}
    </ClientAreaContext.Provider>
  );
}

export function useClientArea() {
  return useContext(ClientAreaContext);
}
